import React from "react";

function CleanTable({
	className = "",
	showActionsTitle = false,
	tableData = [],
	tableColumns = [{}],
	hasDelete = false,
	onDelete = () => false,
	deleteHtml = () => false,
	hasEdit,
	onEdit,
	editHtml = () => false,
	groupActions,
	allowCheckboxes,
	selectedItems = [],
	setSelectedItems,
	bulkActions = () => false,
	onColumnClick = () => false,
	onRowClick = () => false,
	enablePagination = { total: 0, currentPage: 0, setCurrentPage: 0, itemsPerPage: 10 },
	getRowClassName = () => "",
}) {
	if (!tableData.length || tableData[0]?.total_record === 0) {
		return (
			<p style={{ padding: "30px 0", margin: "0 auto", textAlign: "center" }}>
				{tableData.length === 0 ? "Loading..." : "No results found."}
			</p>
		);
	}

	const shouldShowActions = hasDelete || hasEdit;

	const itemsPerPage = enablePagination.itemsPerPage;
	const totalItems = enablePagination.total;

	// Calculate the number of pages
	const pageCount = Math.ceil(totalItems / itemsPerPage);

	// Calculate the starting index
	const startIndex = enablePagination.currentPage * itemsPerPage + 1;

	// Calculate the ending index
	let endIndex = startIndex + itemsPerPage - 1;
	if (endIndex > totalItems) endIndex = totalItems;

	const goToPage = (page) => {
		enablePagination.setCurrentPage(page);
	};

	const getPaginationItems = () => {
		const current = enablePagination.currentPage;
		const total = pageCount;
		let start = Math.max(current - 1, 0);
		let end = Math.min(current + 2, total);

		if (current === 0) {
			end = Math.min(3, total); // Show the first three pages
		} else if (current === total - 1) {
			start = Math.max(total - 3, 0); // Show the last three pages
		}

		const buttons = [];
		if (start > 0) {
			buttons.push(
				<button
					style={{ minWidth: "48px" }}
					key='first'
					onClick={() => goToPage(0)}
					className='btn numaric primary'
				>
					1
				</button>,
			);
		}
		for (let i = start; i < end; i++) {
			buttons.push(
				<button
					style={{ minWidth: "48px" }}
					key={i}
					onClick={() => goToPage(i)}
					disabled={i === current}
					className='btn numaric primary'
				>
					{i + 1}
				</button>,
			);
		}
		// Insert ellipsis if the current page set doesn't include the last page
		if (current < 3 && total > 4) {
			buttons.splice(
				3,
				0,
				<span style={{ display: "flex", alignItems: "end" }} key='ellipsis' className='ellipsis'>
					...
				</span>,
			);
		}
		if (current > 3 && total > 4) {
			buttons.splice(
				1,
				0,
				<span style={{ display: "flex", alignItems: "end" }} key='ellipsis' className='ellipsis'>
					...
				</span>,
			);
		}
		return buttons;
	};

	// Toggle selected items
	const toggleSelectedItem = (item) => {
		const itemExists = selectedItems.some((selectedItem) => selectedItem.id === item.id);
		if (itemExists) {
			// Remove item from selection
			setSelectedItems(selectedItems.filter((selectedItem) => selectedItem.id !== item.id));
		} else {
			// Add item to selection
			setSelectedItems([...selectedItems, item]);
		}
	};

	// Handle "select all" checkbox
	const areAllSelected = tableData.every((item) => selectedItems.some((selectedItem) => selectedItem.id === item.id));
	// Toggle "select all" checkbox
	const toggleSelectAll = () => {
		if (areAllSelected) {
			// If all are selected, deselect all
			setSelectedItems([]);
		} else {
			// If not all are selected, select all
			setSelectedItems(tableData);
		}
	};

	return (
		<>
			<table className={className}>
				<thead>
					<tr>
						{allowCheckboxes && (
							<th className='select-boxes'>
								<input
									type='checkbox'
									checked={areAllSelected}
									onChange={toggleSelectAll}
									onClick={(e) => e.stopPropagation()} // Prevent header sort on checkbox change
								/>
							</th>
						)}
						{tableColumns.map((col, index) => (
							<th
								className={col.renderColumn ? "pointer action-header" : `normal  col-${index}`}
								key={index}
								onClick={() => onColumnClick(col)}
							>
								{col.renderColumn ? col.renderColumn(col) : col.header}
							</th>
						))}
						{shouldShowActions && (
							<th className='action-header'>
								{showActionsTitle
									? "Actions"
									: typeof bulkActions === "function"
									? bulkActions()
									: bulkActions}
							</th>
						)}
					</tr>
				</thead>
				<tbody>
					{tableData.map((item, index) => (
						<tr key={index} className={getRowClassName(item)}>
							{allowCheckboxes && (
								<td className={`col-${index} select-boxes`}>
									<input
										type='checkbox'
										checked={selectedItems.some((selectedItem) => selectedItem.id === item.id)}
										onChange={(event) => {
											event.stopPropagation();
											toggleSelectedItem(item);
										}}
									/>
								</td>
							)}
							{tableColumns.map((col, colIndex) => (
								<td
									className={`data-columns col-${colIndex}`}
									key={colIndex}
									onClick={() => onRowClick(item)}
								>
									{col.render ? col.render(item) : item[col.accessor]}
								</td>
							))}
							{shouldShowActions && (
								<td className={`action-columns col-${index}`}>
									{groupActions ? (
										groupActions(item)
									) : (
										<>
											{hasDelete && (
												<span
													onClick={(e) => {
														e.stopPropagation();
														onDelete(item);
													}}
												>
													{deleteHtml}
												</span>
											)}
											{hasEdit && (
												<span
													onClick={(e) => {
														e.stopPropagation();
														onEdit(item);
													}}
												>
													{editHtml(item)}
												</span>
											)}
										</>
									)}
								</td>
							)}
						</tr>
					))}
				</tbody>
			</table>
			{enablePagination.total > 0 && (
				<div style={{ padding: "20px 30px 15px" }}>
					<div className='row table-footer'>
						<div className='col col-4' style={{ display: "flex", alignItems: "center" }}>
							{startIndex ? "Showing" : ""} {startIndex ? `${startIndex} - ` : ""}
							{endIndex ? `${endIndex} of` : ""} {totalItems} total
						</div>
						{itemsPerPage < totalItems && (
							<div
								className='col col-8'
								style={{ textAlign: "right", display: "flex", justifyContent: "flex-end", gap: "9px" }}
							>
								<button
									className='btn primary'
									onClick={() => goToPage(enablePagination.currentPage - 1)}
									disabled={enablePagination.currentPage === 0}
								>
									Previous
								</button>
								{getPaginationItems()}
								<button
									className='btn primary'
									onClick={() => goToPage(enablePagination.currentPage + 1)}
									disabled={enablePagination.currentPage >= pageCount - 1}
								>
									Next
								</button>
							</div>
						)}
					</div>
				</div>
			)}
		</>
	);
}

export { CleanTable };
